/*
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Badge, Card } from "@farmlands/shadcn-ui";
import { PercentageBar } from "@farmlands/ui-library";
import dayjs from "dayjs";
import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { BASE, BASE_AGREEMENT } from "@/features/sales-agreements/constants";
import { getAgreementExpiryBadgeLabel } from "@/features/sales-agreements/utils/getAgreementExpiryBadgeLabel";
import TestIds from "@/testIds";
import { ScrollYPositionKeys } from "@/util/consts";
import { saveScrollYPosition } from "@/util/helpers";

interface AgreementListItemProps {
  id: string;
  title: string;
  startDate: Date;
  endDate: Date;
  percentageFulfilled: number;
  products: string[];
}

export const AgreementListItem = (props: AgreementListItemProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const agreementTitle = props.title ? props.title : props.id;
  const agreementDatesText = `${dayjs(props.startDate).format("D MMM YYYY")} - ${dayjs(props.endDate).format("D MMM YYYY")}`;
  const expiryBadgeLabel = getAgreementExpiryBadgeLabel(props.endDate);

  const agreementDetailsNavigate = (
    e: React.MouseEvent<HTMLElement, MouseEvent> | null,
    path: string,
    id: string
  ) => {
    const sessionStorageKey = ScrollYPositionKeys.Agreements;
    if (pathname === BASE) {
      saveScrollYPosition({ sessionStorageKey });
    } else {
      saveScrollYPosition({ sessionStorageKey, position: 0 });
    }
    e?.preventDefault();
    navigate(`${path}/${id}`);
  };

  return (
    <Card
      data-testid={TestIds.feat.agreement.card.agreementCard}
      data-id={props.id}
      className="cursor-pointer transition-all hover:shadow-lg"
    >
      <div
        className="py-4s flex flex-col items-start gap-3 px-8"
        tabIndex={-1}
        onClick={(e) => agreementDetailsNavigate(e, BASE_AGREEMENT, props.id)}
      >
        <div className="flex w-full flex-col gap-2 md:flex-row md:items-center md:gap-2">
          <div className="shrink-0 font-semibold">{agreementTitle}</div>
          <div className="min-w-0 flex-1 overflow-hidden">
            {props.products.slice(0, 1).map((item, index) => {
              if (props.products.length > 1 && index === 0) {
                return (
                  <div
                    className="flex min-w-0 items-center gap-1 md:gap-2"
                    data-testid={TestIds.feat.agreement.card.product}
                    data-id={props.id}
                    key={`${item}_${index}`}
                  >
                    <span className="text-content-primary min-w-0 truncate">{item}</span>
                    <span className="shrink-0 whitespace-nowrap text-muted-foreground">
                      +{props.products.length - 1} more
                    </span>
                  </div>
                );
              }
              return (
                <div
                  className="truncate"
                  data-testid={TestIds.feat.agreement.card.product}
                  data-id={props.id}
                  key={`${item}_${index}`}
                >
                  {item}
                </div>
              );
            })}
          </div>
        </div>
        <PercentageBar
          data-testid={TestIds.feat.agreement.card.fulfillmentBar}
          data-id={props.id}
          percentage={props.percentageFulfilled}
        />
        <div className="flex w-full flex-col justify-between gap-3 sm:flex-row">
          <span className="text-2xs" data-testid={TestIds.feat.agreement.card.date}>
            {agreementDatesText}
          </span>
          {expiryBadgeLabel && (
            <Badge className="shrink-0 font-semibold whitespace-nowrap" variant="warning" size="sm">
              {expiryBadgeLabel}
            </Badge>
          )}
        </div>
      </div>
    </Card>
  );
};
